import type { JSX } from 'solid-js'
import { createEffect, createMemo, createSignal, createUniqueId, mergeProps, Show, splitProps, untrack } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import FieldMessage from '~/components/FieldMessage'
import Icon from '~/components/Icon'
import Tooltip from '~/components/Tooltip'
import { useAppState } from '~/contexts/ThemeContext'
import { useFieldError } from '~/hooks/useFieldError'
import type { SwitchColorType, SwitchPositionType, SwitchPropsType, SwitchSizeType } from '~/types/Switch'

const sizeClasses: Record<SwitchSizeType, { container: string; thumb: string; translate: string; label: string; spacing: string }> = {
  xs: {
    container: 'h-4 w-7',
    thumb: 'h-3 w-3',
    translate: 'translate-x-3',
    label: 'text-xs',
    spacing: 'gap-2'
  },
  sm: {
    container: 'h-5 w-9',
    thumb: 'h-4 w-4',
    translate: 'translate-x-4',
    label: 'text-sm',
    spacing: 'gap-2'
  },
  md: {
    container: 'h-6 w-11',
    thumb: 'h-5 w-5',
    translate: 'translate-x-5',
    label: 'text-base',
    spacing: 'gap-2.5'
  },
  lg: {
    container: 'h-6 w-11',
    thumb: 'h-5 w-5',
    translate: 'translate-x-5',
    label: 'text-lg',
    spacing: 'gap-3'
  },
  xl: {
    container: 'h-8 w-14',
    thumb: 'h-6.5 w-6.5',
    translate: 'translate-x-6.5',
    label: 'text-xl',
    spacing: 'gap-4'
  }
}

const colorClasses = (isDark: boolean): Record<SwitchColorType, { base: string; hover: string; checked: string }> => ({
  primary: {
    base: isDark ? 'bg-gray-700' : 'bg-gray-200',
    hover: isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-300',
    checked: 'bg-primary-500'
  },
  secondary: {
    base: isDark ? 'bg-gray-700' : 'bg-gray-200',
    hover: isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-300',
    checked: 'bg-slate-500'
  },
  success: {
    base: isDark ? 'bg-gray-700' : 'bg-gray-200',
    hover: isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-300',
    checked: 'bg-green-500'
  },
  danger: {
    base: isDark ? 'bg-gray-700' : 'bg-gray-200',
    hover: isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-300',
    checked: 'bg-red-500'
  },
  warning: {
    base: isDark ? 'bg-gray-700' : 'bg-gray-200',
    hover: isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-300',
    checked: 'bg-amber-500'
  },
  info: {
    base: isDark ? 'bg-gray-700' : 'bg-gray-200',
    hover: isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-300',
    checked: 'bg-blue-500'
  },
  default: {
    base: isDark ? 'bg-gray-700' : 'bg-gray-200',
    hover: isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-300',
    checked: 'bg-slate-800'
  }
})

export default function Switch(props: SwitchPropsType): JSX.Element {
  const { isDark } = useAppState()

  const merged = mergeProps(
    {
      size: 'md' as SwitchSizeType,
      color: 'primary' as SwitchColorType,
      position: 'end' as SwitchPositionType,
      trueValue: undefined,
      falseValue: undefined,
      trueIcon: undefined,
      falseIcon: undefined
    },
    props
  )

  const [local, _others] = splitProps(merged, [
    'class',
    'classList',
    'label',
    'color',
    'size',
    'error',
    'helperText',
    'checked',
    'disabled',
    'position',
    'trueValue',
    'falseValue',
    'trueIcon',
    'falseIcon',
    'tooltip',
    'tooltipPlacement'
  ])

  /**
   * `name` no se esparce sobre el checkbox visible: del envío al formulario se
   * encarga el input oculto del final.
   *
   * `onChange` tampoco: lo emite este componente con su evento sintético. Si se
   * esparciera, el consumidor recibiría además el `change` nativo del input y
   * cada pulsación contaría dos veces.
   */
  const [reserved, others] = splitProps(_others, ['name', 'onChange'])

  // Sin prop `error`, el campo lee su error del contexto del `<Form>` (boolean:
  // basta con que exista). La prop explícita gana sobre el contexto.
  const resolvedError = useFieldError(
    () => reserved.name,
    () => local.error
  )
  const hasError = () => Boolean(resolvedError())

  const uid = createUniqueId()
  const [isChecked, setIsChecked] = createSignal(local.checked ?? false)

  /**
   * El estado vive dentro (uso no controlado), pero si el padre cambia `checked`
   * hay que seguirlo: en una matriz de permisos, marcar "eliminar" marca también
   * "editar" desde fuera y ese interruptor se quedaba apagado aunque el dato ya
   * dijera lo contrario. Sólo corre cuando la prop cambia de verdad, así que el
   * uso no controlado (checked como valor inicial) sigue igual.
   */
  createEffect(() => {
    const next = local.checked ?? false
    if (next !== untrack(isChecked)) setIsChecked(next)
  })

  const currentColorClasses = createMemo(() => colorClasses(isDark())[local.color])

  const currentSizeClasses = createMemo(() => sizeClasses[local.size])

  const getCurrentValue = createMemo(() => (isChecked() ? (local.trueValue ?? true) : (local.falseValue ?? false)))
  const showCustomIcon = createMemo(() => (isChecked() && local.trueIcon) || (!isChecked() && local.falseIcon))
  const getCurrentIcon = createMemo(() => (isChecked() ? local.trueIcon : local.falseIcon))
  const getIconSize = createMemo(() => ({ xs: 10, sm: 12, md: 14, lg: 16, xl: 26 })[local.size] || 14)

  const containerClasses = createMemo(() => {
    const color = currentColorClasses()
    const size = currentSizeClasses()

    return twMerge(
      'relative inline-flex shrink-0',
      size.container,
      'rounded-full',
      /**
       * Sombra interna: la pista parece un canal excavado y la bola, encima, flota.
       * No se usa `shadow-inner`: en Tailwind v4 vive en el bloque deprecado de
       * `theme.css` y su valor (0.05 de opacidad) no se ve en una pista de 16–32 px.
       */
      'shadow-[inset_0_2px_4px_rgba(0,0,0,0.12),inset_0_1px_1px_rgba(0,0,0,0.06)]',
      'dark:shadow-[inset_0_2px_4px_rgba(0,0,0,0.45),inset_0_1px_1px_rgba(0,0,0,0.25)]',
      'transition-all duration-150 ease-out',
      hasError() ? 'bg-danger-soft' : isChecked() ? color.checked : color.base,
      local.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      !hasError() && !isChecked() && !local.disabled ? color.hover : '',
      local.class || ''
    )
  })

  const thumbClasses = createMemo(() => {
    const size = currentSizeClasses()
    return twMerge(
      'pointer-events-none',
      'absolute left-0.5 top-1/2 transform -translate-y-1/2',
      'inline-flex items-center justify-center',
      size.thumb,
      'rounded-full',
      /**
       * Elevación difusa hacia abajo (luz cenital). Sólo cuando la bola tiene fondo:
       * con icono no hay superficie que proyecte sombra y el `box-shadow` de una caja
       * transparente se vería como un halo suelto. Ese caso lo resuelve el drop-shadow
       * del propio icono.
       */
      showCustomIcon() ? '' : 'bg-white shadow-[0_2px_5px_rgba(0,0,0,0.14),0_1px_1px_rgba(0,0,0,0.06)]',
      'ring-0',
      'transition-all duration-150 ease-out',
      isChecked() ? size.translate : ''
    )
  })

  /**
   * El icono se pinta directamente sobre la pista, así que su color lo manda el
   * fondo de ésta, no el tema: en blanco fijo desaparecía sobre la pista apagada
   * (gris claro) y sobre el fondo de error. El drop-shadow sólo acompaña al icono
   * blanco; sobre pista clara ensuciaría el trazo.
   */
  const iconClasses = createMemo(() => {
    if (hasError()) return 'text-danger'
    if (isChecked()) return 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]'
    return isDark() ? 'text-gray-300' : 'text-gray-500'
  })

  const labelClasses = createMemo(() => twMerge(currentSizeClasses().label, local.disabled ? 'opacity-50' : '', 'text-foreground-muted'))

  const labelContainerClasses = createMemo(() => twMerge('inline-flex items-center', local.position === 'start' && local.label ? 'flex-row-reverse' : ''))

  /**
   * Un único punto de alternancia: el `change` del checkbox real.
   *
   * Antes el contenedor tenía su propio `onClick` y, como todo vive dentro de un
   * `<label for>`, pulsar sobre el control disparaba dos veces (el clic directo y
   * el que el label reenvía al input): el interruptor se encendía y se apagaba en
   * el mismo gesto, y sólo parecía funcionar al pulsar la etiqueta. Ahora el
   * label activa el input y el input avisa una sola vez.
   */
  const emitChange = (newCheckedState: boolean) => {
    if (local.disabled) return
    setIsChecked(newCheckedState)
    const handler = reserved.onChange
    if (typeof handler === 'function') {
      const value = newCheckedState ? (local.trueValue ?? true) : (local.falseValue ?? false)
      const customEvent = {
        currentTarget: { checked: newCheckedState, value },
        target: { checked: newCheckedState, value }
      } as unknown as Event & { currentTarget: HTMLInputElement; target: HTMLInputElement }
      handler(customEvent)
    }
  }

  /**
   * Función, no variable: el interruptor se pinta con envoltorio (Tooltip) o sin
   * él, y reutilizar el **mismo** nodo en las dos ramas lo mueve entre ellas y
   * descoloca el árbol. Con `tooltip` reactivo —una matriz de permisos donde el
   * aviso "Lo otorga el rol" aparece al cargar el rol— eso dejaba la página en
   * blanco, sin error en consola. Cada rama crea su propio nodo.
   */
  const renderSwitch = () => (
    <div class={containerClasses()} classList={local.classList}>
      <input
        type="checkbox"
        class="absolute h-full w-full cursor-pointer opacity-0"
        disabled={local.disabled}
        checked={isChecked()}
        value={String(getCurrentValue())}
        id={uid}
        aria-describedby={`${uid}-message`}
        aria-invalid={hasError() ? 'true' : undefined}
        onChange={e => emitChange(e.currentTarget.checked)}
        {...others}
      />
      <span class={thumbClasses()}>{showCustomIcon() ? <Icon icon={getCurrentIcon()!} width={getIconSize()} class={iconClasses()} /> : null}</span>
    </div>
  )

  const content = (
    <div class="inline-flex flex-col">
      <div class={twMerge('inline-flex items-center', currentSizeClasses().spacing)}>
        <Show when={local.tooltip} fallback={renderSwitch()}>
          <Tooltip content={local.tooltip!} placement={local.tooltipPlacement || 'top'}>
            {renderSwitch()}
          </Tooltip>
        </Show>
        <Show when={local.label}>
          <span class={labelClasses()}>{local.label}</span>
        </Show>
      </div>
      <FieldMessage id={`${uid}-message`} size={local.size} indent={false} reserve={false} error={resolvedError()} helperText={local.helperText} />
    </div>
  )

  return (
    <label for={uid} class={labelContainerClasses()}>
      {content}
      {/* Input hidden para enviar el valor al formulario */}
      {/* `data-boolean` avisa al `<Form>` de que aquí viaja un booleano, no el
          texto "true"/"false": el esquema espera el tipo de verdad. */}
      <input
        type="hidden"
        name={reserved.name}
        data-boolean={typeof (local.trueValue ?? true) === 'boolean' && typeof (local.falseValue ?? false) === 'boolean' ? '' : undefined}
        value={String(isChecked() ? (local.trueValue ?? true) : (local.falseValue ?? false))}
      />
    </label>
  )
}
